import {
  Component,
  ChangeDetectionStrategy,
  inject,
  computed,
  OnInit,
  DestroyRef,
} from "@angular/core";
import { TranslateModule } from "@ngx-translate/core";
import { ActivatedRoute } from "@angular/router";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { catchError, filter, take } from "rxjs/operators";
import { firstValueFrom, of } from "rxjs";
import { FormlyFieldConfig } from "@ngx-formly/core";
import { marker } from "@biesbjerg/ngx-translate-extract-marker";

import { CompanyCardComponent } from "src/app/shared/components/network-card/company-card.component";
import {
  SearchContainerComponent,
  SearchDataSource,
  SearchParams,
  SearchResult,
  SearchState,
  SearchActionButton,
  SearchFilters,
  buildApiParams,
} from "src/app/shared/components/search";

// API models and services
import { CompanyBase } from "src/app/api/models/company-base";
import { CompanyBasePagedData } from "src/app/api/models/company-base-paged-data";
import { CompanyService } from "src/app/api/services/company.service";
import { GetCompanies$Params } from "src/app/api/fn/company/get-companies";

// Services
import { DomainService } from "src/app/shared/services/domain.service";
import { MatchService } from "src/app/shared/services/match.service";
import { ModalService } from "src/app/shared/services/modal.service";
import { AuthenticationService } from "src/app/shared/services/authentication.service";

import { FilterOptionsService } from "src/app/shared/services/filter-options.service";
import { TargetGroupService } from "src/app/shared/services/target-group.service";
import { ModalStudentCompanyMatchesComponent } from "../modal-student-company-matches/modal-student-company-matches.component";
import { RoleTypes } from "src/app/guards/roletypes";
import { empty } from "src/app/functions";
import { ModelUser } from "src/app/model/model.user";

@Component({
  selector: "app-student-company-search",
  providers: [SearchState],
  imports: [TranslateModule, SearchContainerComponent, CompanyCardComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (userProfile()) {
    <app-search-container
      [config]="searchConfig()"
      [dataSource]="dataSource"
      [fields]="fields()"
      [actionButtons]="actionButtons()"
      [trackByFn]="trackByCompanyId"
      [showMatchFilter]="true"
    >
      <!-- Single template handles both grid (portrait) and map popup (landscape) -->
      <ng-template
        #itemTemplate
        let-company
        let-isHighlighted="isHighlighted"
        let-layout="layout"
      >
        <app-company-card
          [data]="company"
          [roleType]="RoleTypes.Student"
          [isHighlighted]="isHighlighted"
          [showMatchPercentage]="layout === 'landscape'"
          [layout]="layout ?? 'portrait'"
        ></app-company-card>
      </ng-template>
    </app-search-container>
    }
  `,
})
export class StudentCompanySearchComponent implements OnInit {
  private readonly domainService = inject(DomainService);
  private readonly companyService = inject(CompanyService);
  private readonly matchService = inject(MatchService);
  private readonly modalService = inject(ModalService);
  private readonly authService = inject(AuthenticationService);

  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly searchState = inject(SearchState);
  private readonly filterOptionsService = inject(FilterOptionsService);
  private readonly targetGroupService = inject(TargetGroupService);

  // User profile signal — only the first non-null emission, which is what
  // we need for defaultFilters. Template gates the search-container render
  // on this so SearchState.initialize() receives the correct defaultFilters.
  readonly userProfile = toSignal<ModelUser | undefined>(
    this.authService.profile$.pipe(
      filter((u: ModelUser | null): u is ModelUser => !!u),
      take(1)
    )
  );

  // Constants
  readonly RoleTypes = RoleTypes;
  private readonly minimumMatchPercentage =
    this.matchService.minimumMatchPercentageStudentCompanies;
  private readonly studentProfile = toSignal(this.authService.studentprofile$);

  // Formly fields configuration using shared field builders
  // Order matches original: Search → Location → Radius → Area of interests → Amount employees
  fields = computed<FormlyFieldConfig[]>(() => {
    // Read signal inside computed so field config reactively updates when profile loads (#17797)
    const hideRadius = this.targetGroupService.isInternationalOnly();
    return [
      this.filterOptionsService.searchTermField(),
      this.filterOptionsService.buildSearchModeField(() => hideRadius),
      this.filterOptionsService.buildSearchModeLocationField({
        label: "Location company",
      }),
      this.filterOptionsService.buildSearchModeRadiusField(
        this.searchState.searchOptions().radiusOptions,
        () => this.studentProfile() ?? null,
        () => hideRadius
      ),
      this.filterOptionsService.areaOfInterestsField(),
      this.filterOptionsService.targetGroupsField(),
      this.filterOptionsService.employeeRangeField(),
    ];
  });

  // Network filter action buttons
  actionButtons = computed<SearchActionButton[]>(() => [
    {
      id: "all",
      label: "All companies",
      activeCondition: (filters: SearchFilters) =>
        filters.MinimumMatchPercentage == null && filters.Connected == null,
      onClick: (updateFilters) =>
        updateFilters({ MinimumMatchPercentage: null, Connected: null }),
    },
    {
      id: "matching",
      label: "Matching companies",
      activeCondition: (filters: SearchFilters) =>
        (filters.MinimumMatchPercentage as number) >=
        this.minimumMatchPercentage,
      onClick: (updateFilters) =>
        updateFilters({
          MinimumMatchPercentage: this.minimumMatchPercentage,
          Connected: null,
        }),
    },
    {
      id: "network",
      label: "Companies in network",
      activeCondition: (filters: SearchFilters) => filters.Connected === true,
      onClick: (updateFilters) =>
        updateFilters({ Connected: true, MinimumMatchPercentage: null }),
    },
  ]);

  /**
   * Resolve the user's default target groups for the company entity.
   * Respects the disableTargetGroupsCompanies flag. Returns null when
   * filtering should not be applied (no target groups set or user opted out).
   */
  private readonly defaultTargetGroups = computed<string[] | null>(() => {
    const user = this.userProfile();
    if (!user || user.disableTargetGroupsCompanies) return null;
    const groups = user.accountTargetGroups;
    return groups?.length ? groups : null;
  });

  // Simplified search config - only searchOptions and context
  searchConfig = computed(() => ({
    searchOptions: {
      ...this.searchState.searchOptions(),
      orderByOptions: [
        { value: "currentUserMatchPercentage", label: "Match" },
        { value: "name", label: "Name" },
        { value: "date", label: "Date" },
      ],
      orderOptions: {
        ...this.searchState.searchOptions().orderOptions,
        currentUserMatchPercentage: [
          { value: "Descending", label: marker("Highest first") },
          { value: "Ascending", label: marker("Lowest first") },
        ],
      },
    },
    context: {
      queryParamOptions: [
        "Favorites",
        "Location",
        "MaximumDistance",
        "AreaOfInterests",
        "EducationLevels",
        "Connected",
        "SearchTerm",
        "NumberOfEmployeesLower",
        "NumberOfEmployeesUpper",
        "JobPostingType",
        "MinimumMatchPercentage",
        "TargetGroups",
      ],
    },
    filterDisplayNames: {
      SearchTerm: "Search",
      Connected: "Companies in network",
      MaximumDistance: "Distance from me",
      Location: "Location company",
    },
    valueFormatters: {
      MaximumDistance: (value: string | number | boolean) => `${value} km`,
    },
    excludeFromBadges: ["MinimumMatchPercentage", "searchMode"],
    mapConfig: {
      enabled: true,
      getCoordinates: (company: CompanyBase) => {
        const addr = company.visitAddress;
        if (addr?.latitude != null && addr?.longitude != null) {
          return { lat: addr.latitude, lng: addr.longitude };
        }
        return null;
      },
    },
    // Pre-populate TargetGroups based on the user's accountTargetGroups.
    // Applied by SearchState.initialize() inside the child search-container
    // which has its own SearchState provider. The template gates render
    // until userProfile() is non-null so this is always correct.
    defaultFilters: (() => {
      const groups = this.defaultTargetGroups();
      return groups ? { TargetGroups: groups } : {};
    })(),
  }));

  // Data source - real API
  dataSource: SearchDataSource<CompanyBase> = {
    fetch: async (params: SearchParams): Promise<SearchResult<CompanyBase>> => {
      try {
        const apiParams = buildApiParams<GetCompanies$Params>(
          params,
          this.domainService.getCurrentRegion(),
          [
            {
              from: "TargetGroups",
              to: "CompanyTargetGroups",
              condition: (v) => v?.length > 0,
            },
          ]
        );

        const result = await firstValueFrom(
          this.companyService
            .getCompanies(apiParams)
            .pipe(
              catchError(() =>
                of({ data: [], totalItems: 0 } as CompanyBasePagedData)
              )
            )
        );

        return {
          data: result?.data || [],
          totalItems: result?.totalItems || 0,
          pageNumber: params.pageNumber,
          pageSize: params.pageSize,
        };
      } catch (error) {
        console.error("Error fetching companies:", error);
        return {
          data: [],
          totalItems: 0,
          pageNumber: params.pageNumber,
          pageSize: params.pageSize,
        };
      }
    },
  };

  ngOnInit(): void {
    this.checkShowMatchesModal();
  }

  private checkShowMatchesModal(): void {
    // Check if we need to show a popup with matches (when student just registered)
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        if (!empty(params["showMatches"])) {
          this.authService.profile$.pipe(take(1)).subscribe((account) => {
            if (account) {
              const modalData = {
                student_profile_id: account["id"],
              };

              const modalOptions = {
                size: "xl",
              };

              this.modalService.Modal(
                ModalStudentCompanyMatchesComponent,
                "",
                "",
                modalData,
                [],
                modalOptions
              );
            }
          });
        }
      });
  }

  readonly trackByCompanyId = (_index: number, company: CompanyBase): string =>
    company.id || "";
}
